/**
 * @module constants
 * @description Constants used across the application
 */

// TODO: place these day/date constants in the /utils module

export const WEEKDAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
];

export const WEEKENDS = ['Saturday', 'Sunday'];

export const RELATIVE_DAYS = {
    TODAY: 'Today',
};

/**
 * An array with the names of the days of the week
 * @property {string} 0="Monday" - Monday
 * @property {string} 1="Tuesday" - Tuesday
 * @property {string} 2="Wednesday" - Wednesday
 * @property {string} 3="Thursday" - Thursday
 * @property {string} 4="Friday" - Friday
 * @property {string} 5="Saturday" - Saturday
 * @property {string} 6="Sunday" - Sunday
 */
export const DAYS_OF_WEEK = [...WEEKDAYS, ...WEEKENDS];

/**
 * Takes the DAYS_OF_WEEK array and returns an object with the days of the week as keys and the their abbreviations as values
 * @property {object} monday - The day of the week and its abbreviation
 * @property {string} monday.label="mon" - abbreviation for Monday
 * @property {object} tuesday - The day of the week and its abbreviation
 * @property {string} tuesday.label="tue" - abbreviation for Tuesday
 * @property {object} wednesday - The day of the week and its abbreviation
 * @property {string} wednesday.label="wed" - abbreviation for Wednesday
 * @property {object} thursday - The day of the week and its abbreviation
 * @property {string} thursday.label="thu" - abbreviation for Thursday
 * @property {object} friday - The day of the week and its abbreviation
 * @property {string} friday.label="fri" - abbreviation for Friday
 * @property {object} saturday - The day of the week and its abbreviation
 * @property {string} saturday.label="sat" - abbreviation for Saturday
 * @property {object} sunday - The day of the week and its abbreviation
 * @property {string} sunday.label="sun" - abbreviation for Sunday
 * @return {object} - An object with the days of the week as keys and their abbreviations as values
 */
export const DAYS_WITH_LABELS = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day.toLowerCase()] = {
        label: day.substring(0, 3),
    };

    return acc;
}, {});

/**
 * An array of the possible transient pricing algorithms returned from the facilities endpoint
 * @property {string} 0="matrix_smart_rates"
 * @property {string} 1="matrix_flex_rates"
 * @property {string} 2="matrix_precision_pricing"
 * @property {string} 3="matrix_iq"
 * @property {string} 4="legacy"
 * @property {string} 5="rate_experiment"
 */
export const TRANSIENT_PRICING_ALGORITHMS = /** @type {const} */ ([
    'matrix_smart_rates',
    'matrix_flex_rates',
    'matrix_precision_pricing',
    'matrix_iq',
    'legacy',
    'rate_experiment',
]);

/**
 * An object with the types of transient pricing algorithms as keys and an array of the transient pricing algorithm names that fall into that category as values
 * @type {object}
 * @property {string[]} flexRates - The algorithm name(s) for Flex Rates; Current response from the endpoint returns `matrix_smart_rates`, but will be updated to `matrix_flex_rates` in the future
 * @property {string[]} spotHeroIQ - The algorithm name(s) for SpotHero IQ; Current response from the endpoint returns `matrix_precision_pricing`, but will be updated to `matrix_iq` in the future
 * @property {string[]} legacy - The algorithm name(s) for Legacy Rates
 * @property {string[]} rateExperiment - The algorithm name(s) for the Rate Experiment; When the Rate Experiment is present, all of the applicable pricing algorithms in that rate experiment will be returned alongside the `rate_experiment` algorithm
 */
export const CATEGORIZED_ALGORITHMS = {
    flexRates: ['matrix_smart_rates', 'matrix_flex_rates'],
    spotHeroIQ: ['matrix_precision_pricing', 'matrix_iq'],
    legacy: ['legacy'],
    rateExperiment: ['rate_experiment'],
};

/**
 * An object containing the types of interpolation algorithms
 * @type {object}
 * @property {string} logarithmic - The logarithmic interpolation algorithm
 * @property {string} linear - The linear interpolation algorithm
 * @property {string} none - No interpolation algorithm
 */
export const INTERPOLATION_ALGORITHMS = {
    logarithmic: 'log',
    linear: 'linear',
    none: 'none',
};
